import React, { useEffect, useState } from "react";
import { datadogRum } from "@datadog/browser-rum";
import Dropdown from "../DropDown";
import { fetchWithCache } from "../../CacheDB/cached-Ref-Api";

interface SchedulingTypeData {
  scheduling_type_code: string;
  disabled: boolean;
}

interface SchedulingTypeDropdownProps {
  selectedSchedulingType: string;
  onSchedulingTypeChange: (
    selectedSchedulingType: string,
    selectedSchedulingTypeObject?: SchedulingTypeData
  ) => void;
  disabled: boolean;
  className?: string;
  onFocus?: () => void;
}

const SchedulingTypeDropdown: React.FC<SchedulingTypeDropdownProps> = ({
  selectedSchedulingType,
  onSchedulingTypeChange,
  disabled,
  className = "",
  onFocus,
}) => {
  const [schedulingTypeData, setSchedulingTypeData] = useState<SchedulingTypeData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      const startTime = performance.now();
      try {
        datadogRum.addAction("SchedulingTypeDropdown Mounted", {
          component: "SchedulingTypeDropdown",
        });

        const data = await fetchWithCache("scheduling-type", "/ref-data/scheduling-type");
        setSchedulingTypeData(data);

        datadogRum.addAction("Fetched Scheduling Types (Cached/Live)", {
          schedulingTypeCount: data.length,
        });
      } catch (error: any) {
        setError("Failed to fetch Scheduling Types");
        datadogRum.addError("Scheduling Type API Fetch Error", {
          error: error?.message || error,
        });
      } finally {
        setLoading(false);
        const endTime = performance.now();
        datadogRum.addAction("Scheduling Type Fetch Duration", {
          duration: endTime - startTime,
        });
      }
    };

    loadData();
  }, []);

  const options = schedulingTypeData.map((type) => ({
    value: type.scheduling_type_code,
    label: type.scheduling_type_code,
    disabled: type.disabled,
  }));

  const handleChange = (value: string) => {
    const selected = schedulingTypeData.find(
      (item) => item.scheduling_type_code === value
    );
    if (selected) {
      onSchedulingTypeChange(value, selected);
      datadogRum.addAction("Scheduling Type Selected", selected);
    }
  };

  return (
    <Dropdown
      options={options}
      selectedValue={selectedSchedulingType}
      onValueChange={handleChange}
      disabled={disabled || loading || !!error}
      className={className}
      placeholder={loading ? "Loading Scheduling Types..." : error ?? "Select Scheduling Type"}
      onFocus={onFocus}
    />
  );
};

export default SchedulingTypeDropdown;
